// ════════════════════════════════════════════════════════════
// OfertasComerciales — ofertas de máquinas y proyectos
// Todos los campos de condiciones son de texto libre.
// ════════════════════════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button, Chip, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Select, MenuItem,
  FormControl, InputLabel, Autocomplete, Divider, FormControlLabel, Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getOfertasComerciales, getOfertaComercial, crearOfertaComercial, actualizarOfertaComercial,
  cambiarEstadoOfertaComercial, duplicarOfertaComercial, borrarOfertaComercial,
  proformaDesdeOfertaComercial, facturarOfertaComercial, descargarOfertaComercialPDF,
  getClientes
} from '../api';

const ESTADOS: any = {
  borrador:  { label: 'Borrador',  color: 'default' },
  enviada:   { label: 'Enviada',   color: 'info' },
  aceptada:  { label: 'Aceptada',  color: 'success' },
  rechazada: { label: 'Rechazada', color: 'error' },
};
const eur = (n: number) => (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
const r2 = (n: number) => Math.round(n * 100) / 100;
const LINEA = { concepto: '', descripcion: '', cantidad: 1, precio_unitario: 0, descuento: 0, tipo_iva: 21 };

export default function OfertasComerciales({ oportunidades }: any) {
  const [items, setItems] = useState<any[]>([]);
  const [plantillas, setPlantillas] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [vista, setVista] = useState(0);
  const [search, setSearch] = useState('');
  const [cargando, setCargando] = useState(true);
  const [editor, setEditor] = useState<any>(null);
  const [lineas, setLineas] = useState<any[]>([]);
  const [procesando, setProcesando] = useState(false);

  const cargar = () => {
    setCargando(true);
    getOfertasComerciales({ search }).then(setItems).catch(() => setItems([]))
      .finally(() => setCargando(false));
    getOfertasComerciales({ plantillas: 1 }).then(setPlantillas).catch(() => setPlantillas([]));
  };
  useEffect(() => { getClientes().then(setClientes).catch(() => setClientes([])); }, []);
  useEffect(() => {
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const abiertas = items.filter((x: any) => ['borrador', 'enviada'].includes(x.estado));
  const cerradas = items.filter((x: any) => ['aceptada', 'rechazada'].includes(x.estado));
  const lista = vista === 0 ? abiertas : vista === 1 ? cerradas : vista === 2 ? items : plantillas;

  const nueva = (base: any = null) => {
    setEditor({
      id: null, es_plantilla: false, cliente_id: null, contacto: '', titulo: '',
      fecha: new Date().toISOString().slice(0, 10), validez_dias: 30,
      plazo_entrega: base?.plazo_entrega || '', garantia: base?.garantia || '',
      lugar_entrega: base?.lugar_entrega || '', condiciones_pago: base?.condiciones_pago || '',
      forma_pago: base?.forma_pago || '', descuento_general: 0, notas: base?.notas || '',
      oportunidad_id: null, estado: 'borrador', nombre_plantilla: '',
    });
    setLineas(base?.lineas ? base.lineas.map((l: any) => ({ ...l, id: undefined })) : [{ ...LINEA }]);
  };

  const abrir = async (o: any) => {
    try {
      const d = await getOfertaComercial(o.id);
      setEditor({ ...d, es_plantilla: !!d.es_plantilla });
      setLineas((d.lineas || []).length ? d.lineas.map((l: any) => ({ ...l })) : [{ ...LINEA }]);
    } catch { alert('No se pudo cargar la oferta'); }
  };

  const usarPlantilla = async (p: any) => {
    try {
      const d = await getOfertaComercial(p.id);
      nueva(d);
    } catch { alert('No se pudo cargar la plantilla'); }
  };

  const setLinea = (i: number, campo: string, valor: any) => {
    const nuevas = [...lineas];
    nuevas[i] = { ...nuevas[i], [campo]: valor };
    setLineas(nuevas);
  };

  let base = 0;
  let cuota = 0;
  lineas.forEach(l => {
    const b = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
      * (1 - (parseFloat(l.descuento) || 0) / 100);
    base += b;
    cuota += b * (parseFloat(l.tipo_iva) || 0) / 100;
  });
  const dtoG = parseFloat(editor?.descuento_general) || 0;
  base = r2(base * (1 - dtoG / 100));
  cuota = r2(cuota * (1 - dtoG / 100));

  const guardar = async () => {
    if (!editor.es_plantilla && !editor.cliente_id) { alert('Selecciona el cliente'); return; }
    if (editor.es_plantilla && !(editor.nombre_plantilla || '').trim()) {
      alert('Ponle nombre a la plantilla'); return;
    }
    if (!lineas.some(l => (l.concepto || '').trim())) { alert('Añade al menos una línea'); return; }
    setProcesando(true);
    try {
      const payload = {
        ...editor,
        descuento_general: dtoG,
        lineas: lineas.filter(l => (l.concepto || '').trim()).map(l => ({
          concepto: l.concepto.trim(), descripcion: l.descripcion,
          cantidad: parseFloat(l.cantidad) || 0,
          precio_unitario: parseFloat(l.precio_unitario) || 0,
          descuento: parseFloat(l.descuento) || 0, tipo_iva: parseFloat(l.tipo_iva) || 0,
        })),
      };
      if (editor.id) {
        await actualizarOfertaComercial(editor.id, payload);
      } else {
        const r = await crearOfertaComercial(payload);
        if (r.numero) alert(`✅ Oferta ${r.numero} creada`);
      }
      setEditor(null);
      cargar();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar la oferta');
    } finally { setProcesando(false); }
  };

  const cambiarEstado = async (o: any, estado: string) => {
    let motivo = '';
    if (estado === 'rechazada') {
      motivo = window.prompt('Motivo del rechazo:') || '';
      if (!motivo) return;
    }
    if (estado === 'aceptada' && !window.confirm(
      `¿Marcar la oferta ${o.numero} como aceptada?\n\n` +
      `Si está vinculada a una oportunidad, se cerrará como ganada.`)) return;
    try {
      await cambiarEstadoOfertaComercial(o.id, estado, motivo);
      setEditor(null);
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const guardarComoPlantilla = async (o: any) => {
    const nombre = window.prompt('Nombre de la plantilla:', o.titulo || '');
    if (!nombre) return;
    try {
      await duplicarOfertaComercial(o.id, { como_plantilla: true, nombre_plantilla: nombre });
      alert('✅ Plantilla guardada');
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const generarProforma = async (o: any) => {
    try {
      const r = await proformaDesdeOfertaComercial(o.id);
      alert(`✅ Proforma ${r.numero} creada. La tienes en Facturación → Proformas.`);
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const facturar = async (o: any) => {
    if (!window.confirm(`¿Emitir la factura de la oferta ${o.numero} por ${eur(o.total)}?`)) return;
    try {
      const r = await facturarOfertaComercial(o.id);
      alert(`✅ Factura ${r.numero} emitida`);
      setEditor(null);
      cargar();
    } catch (e: any) { alert(e.response?.data?.detail || 'Error al facturar'); }
  };

  const borrar = async (o: any) => {
    if (!window.confirm(`¿Eliminar ${o.numero || o.nombre_plantilla}?`)) return;
    try { await borrarOfertaComercial(o.id); setEditor(null); cargar(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  const campoTexto = (clave: string, etiqueta: string, ancho: number = 6, filas: number = 2) => (
    <Grid item xs={12} md={ancho}>
      <TextField fullWidth size="small" multiline rows={filas} label={etiqueta}
        value={editor?.[clave] || ''} disabled={editor?.estado === 'aceptada'}
        onChange={(e) => setEditor({ ...editor, [clave]: e.target.value })} />
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Buscar (nº, cliente o título)" sx={{ flex: 1, minWidth: 220 }}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => nueva()}
          sx={{ bgcolor: '#4f46e5' }}>
          Nueva oferta
        </Button>
      </Box>

      <Tabs value={vista} onChange={(_, v) => setVista(v)} sx={{ mb: 2 }}
        variant="scrollable" scrollButtons="auto">
        <Tab label={`En curso (${abiertas.length})`} />
        <Tab label={`Cerradas (${cerradas.length})`} />
        <Tab label="Todas" />
        <Tab label={`⭐ Plantillas (${plantillas.length})`} />
      </Tabs>

      {cargando ? <CircularProgress /> : lista.length === 0 ? (
        <Alert severity="info">
          {vista === 3
            ? 'No hay plantillas. Guarda una oferta como plantilla para reutilizarla.'
            : 'No hay ofertas. Pulsa «Nueva oferta» para crear la primera.'}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{vista === 3 ? 'Plantilla' : 'Número'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Título</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Válida hasta</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                {vista !== 3 && <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.map((o: any) => {
                const est = ESTADOS[o.estado] || { label: o.estado, color: 'default' };
                return (
                  <TableRow key={o.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {vista === 3 ? (o.nombre_plantilla || '—') : (o.numero || '—')}
                    </TableCell>
                    <TableCell>{o.cliente_nombre || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden' }}>
                        {o.titulo || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' },
                      color: o.caducada ? 'error.main' : 'inherit' }}>
                      {vista === 3 ? '—' : `${o.fecha_validez || '—'}${o.caducada ? ' · caducada' : ''}`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{eur(o.total)}</TableCell>
                    {vista !== 3 && (
                      <TableCell><Chip size="small" label={est.label} color={est.color} /></TableCell>
                    )}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {vista === 3 ? (
                        <>
                          <Button size="small" variant="contained" sx={{ bgcolor: '#4f46e5' }}
                            onClick={() => usarPlantilla(o)}>Usar</Button>
                          <IconButton size="small" color="error" onClick={() => borrar(o)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Button size="small"
                            onClick={() => descargarOfertaComercialPDF(o.id, o.numero)}>📄 PDF</Button>
                          <Button size="small" variant="contained" sx={{ bgcolor: '#4f46e5' }}
                            onClick={() => abrir(o)}>Abrir</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* EDITOR */}
      <Dialog open={!!editor} maxWidth="lg" fullWidth
        onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setEditor(null); }}>
        {editor && (() => {
          const cerrada = editor.estado === 'aceptada';
          return (
            <>
              <DialogTitle>
                {editor.es_plantilla
                  ? `⭐ Plantilla ${editor.nombre_plantilla || ''}`
                  : editor.id ? `📊 Oferta ${editor.numero || ''}` : '📊 Nueva oferta comercial'}
                {editor.id && !editor.es_plantilla && (
                  <Chip size="small" sx={{ ml: 1 }} label={ESTADOS[editor.estado]?.label}
                    color={ESTADOS[editor.estado]?.color} />
                )}
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
                  {!editor.id && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={!!editor.es_plantilla}
                          onChange={(e) => setEditor({ ...editor, es_plantilla: e.target.checked })} />}
                        label="Guardar como plantilla (sin cliente ni numeración)" />
                    </Grid>
                  )}
                  {editor.es_plantilla ? (
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth size="small" label="Nombre de la plantilla *"
                        value={editor.nombre_plantilla || ''}
                        onChange={(e) => setEditor({ ...editor, nombre_plantilla: e.target.value })} />
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12} md={5}>
                        <Autocomplete options={clientes} disabled={cerrada}
                          getOptionLabel={(c: any) => c.nombre || ''}
                          value={clientes.find((c: any) => c.id === editor.cliente_id) || null}
                          onChange={(_, v: any) => setEditor({ ...editor, cliente_id: v ? v.id : null })}
                          renderInput={(p) => <TextField {...p} size="small" label="Cliente *" />} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Persona de contacto" disabled={cerrada}
                          value={editor.contacto || ''}
                          onChange={(e) => setEditor({ ...editor, contacto: e.target.value })} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" type="date" label="Fecha" disabled={cerrada}
                          InputLabelProps={{ shrink: true }} value={editor.fecha || ''}
                          onChange={(e) => setEditor({ ...editor, fecha: e.target.value })} />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <TextField fullWidth size="small" type="number" label="Validez (días)"
                          disabled={cerrada} value={editor.validez_dias || 30}
                          onChange={(e) => setEditor({ ...editor, validez_dias: e.target.value })} />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={editor.es_plantilla ? 6 : 12}>
                    <TextField fullWidth size="small" label="Título de la oferta" disabled={cerrada}
                      placeholder="Ej: Suministro de grúa autopropulsada SANY SAC2500E"
                      value={editor.titulo || ''}
                      onChange={(e) => setEditor({ ...editor, titulo: e.target.value })} />
                  </Grid>
                  {!editor.es_plantilla && oportunidades?.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small" disabled={cerrada}>
                        <InputLabel>Oportunidad vinculada (opcional)</InputLabel>
                        <Select value={editor.oportunidad_id || ''} label="Oportunidad vinculada (opcional)"
                          onChange={(e) => setEditor({ ...editor, oportunidad_id: e.target.value || null })}>
                          <MenuItem value="">— Sin vincular —</MenuItem>
                          {oportunidades.map((op: any) => (
                            <MenuItem key={op.id} value={op.id}>{op.titulo}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Líneas de la oferta</Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Concepto y descripción</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Cant.</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Precio</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Dto %</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 90 }}>IVA</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Importe</TableCell>
                        {!cerrada && <TableCell sx={{ width: 50 }} />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineas.map((l, i) => {
                        const importe = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio_unitario) || 0)
                          * (1 - (parseFloat(l.descuento) || 0) / 100);
                        return (
                          <TableRow key={i}>
                            <TableCell>
                              <TextField fullWidth variant="standard" placeholder="Concepto"
                                disabled={cerrada} value={l.concepto || ''}
                                onChange={(e) => setLinea(i, 'concepto', e.target.value)} />
                              <TextField fullWidth variant="standard" multiline placeholder="Descripción, configuración, accesorios..."
                                disabled={cerrada} value={l.descripcion || ''}
                                sx={{ mt: 0.5, '& .MuiInputBase-input': { fontSize: 12.5 } }}
                                onChange={(e) => setLinea(i, 'descripcion', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" disabled={cerrada}
                                value={l.cantidad} onChange={(e) => setLinea(i, 'cantidad', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" disabled={cerrada}
                                value={l.precio_unitario}
                                onChange={(e) => setLinea(i, 'precio_unitario', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <TextField variant="standard" type="number" disabled={cerrada}
                                value={l.descuento} onChange={(e) => setLinea(i, 'descuento', e.target.value)} />
                            </TableCell>
                            <TableCell>
                              <Select variant="standard" fullWidth disabled={cerrada}
                                value={Number(l.tipo_iva)}
                                onChange={(e) => setLinea(i, 'tipo_iva', e.target.value)}>
                                {[21, 10, 4, 0].map(p => <MenuItem key={p} value={p}>{p}%</MenuItem>)}
                              </Select>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{eur(importe)}</TableCell>
                            {!cerrada && (
                              <TableCell>
                                <IconButton size="small" color="error"
                                  onClick={() => setLineas(lineas.filter((_, j) => j !== i))}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!cerrada && (
                  <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }}
                    onClick={() => setLineas([...lineas, { ...LINEA }])}>
                    Añadir línea
                  </Button>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Condiciones</Typography>
                <Grid container spacing={2}>
                  {campoTexto('plazo_entrega', 'Plazo de entrega')}
                  {campoTexto('garantia', 'Garantía')}
                  {campoTexto('lugar_entrega', 'Lugar de entrega')}
                  {campoTexto('condiciones_pago', 'Condiciones de pago')}
                  {campoTexto('notas', 'Notas y condiciones adicionales', 12, 3)}
                </Grid>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={7}>
                    <TextField size="small" type="number" label="Descuento general %"
                      disabled={cerrada} value={editor.descuento_general || 0}
                      onChange={(e) => setEditor({ ...editor, descuento_general: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Base imponible</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(base)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">IVA</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(cuota)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography sx={{ fontWeight: 700 }}>TOTAL</Typography>
                        <Typography sx={{ fontWeight: 800 }}>{eur(base + cuota)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {editor.id && !cerrada && (
                  <Button color="error" onClick={() => borrar(editor)}>Eliminar</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setEditor(null)}>Cerrar</Button>
                {editor.id && !editor.es_plantilla && (
                  <>
                    <Button onClick={() => descargarOfertaComercialPDF(editor.id, editor.numero)}>
                      📄 PDF
                    </Button>
                    <Button onClick={() => guardarComoPlantilla(editor)}>⭐ Plantilla</Button>
                  </>
                )}
                {!cerrada && (
                  <Button variant="outlined" onClick={guardar} disabled={procesando}>
                    {editor.id ? 'Guardar cambios' : 'Crear'}
                  </Button>
                )}
                {editor.id && !editor.es_plantilla && editor.estado === 'borrador' && (
                  <Button variant="outlined" onClick={() => cambiarEstado(editor, 'enviada')}>
                    Marcar enviada
                  </Button>
                )}
                {editor.id && !editor.es_plantilla && ['borrador', 'enviada'].includes(editor.estado) && (
                  <>
                    <Button color="error" onClick={() => cambiarEstado(editor, 'rechazada')}>
                      Rechazada
                    </Button>
                    <Button variant="contained" sx={{ bgcolor: '#22c55e' }}
                      onClick={() => cambiarEstado(editor, 'aceptada')}>
                      ✅ Aceptada
                    </Button>
                  </>
                )}
                {editor.id && editor.estado === 'aceptada' && !editor.factura_id && (
                  <>
                    <Button variant="outlined" onClick={() => generarProforma(editor)}>
                      📄 Proforma
                    </Button>
                    <Button variant="contained" sx={{ bgcolor: '#4f46e5' }}
                      onClick={() => facturar(editor)}>
                      🧾 Facturar
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
}
